/*
 * File Path: client/src/components/dashboard/main/DashboardContent.js
 * Description: Main content area for the dashboard. Loads initial device state directly from the database on startup.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * Version: 3.5.0
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-06-22
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../common/LoadingSpinner.js';
import { fetchGroupedDevices, updateDevice } from '../../../api/authApi';
import DeviceCard from './DeviceCard';
import './DashboardContent.css';

const POLLING_INTERVAL_MS = 400; // 400 milliseconds polling interval for device state updates

function DashboardContent() {
    const { t } = useTranslation();
    const [groupedDevices, setGroupedDevices] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Direct Data Loading from Database via API ---
    const loadDevices = useCallback(async () => {
        try {
            const flatDeviceList = await fetchGroupedDevices();
            const grouped = flatDeviceList.reduce((acc, device) => {
                const propId = device.property_id;
                if (!acc[propId]) {
                    acc[propId] = { name: device.property_name, isOwner: device.is_property_owner, permissions: device.shared_permissions || [], devices: [] };
                }
                acc[propId].devices.push(device);
                return acc;
            }, {});
            setGroupedDevices(grouped);
            setError(null);
        } catch (err) {
            console.error("[DashboardContent] Error loading devices:", err);
            setError(err.message || t('errors.api.requestFailed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    // --- useEffect for Initial Load and Periodic Polling ---
    useEffect(() => {
        // Load the device state from the database when the component mounts.
        loadDevices();
        // Set up an interval to periodically re-fetch the state as a fallback.
        const intervalId = setInterval(loadDevices, POLLING_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [loadDevices]);


    const handleToggleDevice = useCallback(async (device, subComponentState = null) => {
        const { device_id, device_state, device_type } = device;
        let newStatePayload;

        if (subComponentState) {
            newStatePayload = subComponentState;
        } else {
            const currentState = String(device_state).toUpperCase();
            const newState = (currentState === 'ON' || currentState === 'TRUE') ? 'OFF' : 'ON';
            newStatePayload = { state: newState };
        }

        // Optimistic UI Update
        setGroupedDevices(currentGroups => {
            const newGroups = JSON.parse(JSON.stringify(currentGroups));
            for (const propId in newGroups) {
                const deviceIndex = newGroups[propId].devices.findIndex(d => d.device_id === device_id);
                if (deviceIndex !== -1) {
                    const deviceToUpdate = newGroups[propId].devices[deviceIndex];
                    if (subComponentState && device_type === 'light_bulbs') {
                        const currentStateString = deviceToUpdate.device_state || 'false,false';
                        const states = currentStateString.split(',').map(s => s.trim().toLowerCase() === 'true');
                        if (subComponentState.bulb1 !== undefined) states[0] = subComponentState.bulb1 === 'ON';
                        if (subComponentState.bulb2 !== undefined) states[1] = subComponentState.bulb2 === 'ON';
                        deviceToUpdate.device_state = states.join(',');
                    } else {
                        deviceToUpdate.device_state = newStatePayload.state;
                    }
                    break;
                }
            }
            return newGroups;
        });

        try {
            await updateDevice(device_id, newStatePayload);
            // We no longer need to call loadDevices() here immediately,
            // as the polling will eventually sync the state.
        } catch (err) {
            console.error(`[DashboardContent] Error toggling device ${device_id}:`, err);
            setError(err.message);
            // Revert on error by reloading from the DB.
            loadDevices();
        }
    }, [loadDevices]);

    const sortedPropertyIds = useMemo(() => {
        return Object.keys(groupedDevices).sort((a, b) => {
            const propA = groupedDevices[a];
            const propB = groupedDevices[b];
            if (propA.isOwner && !propB.isOwner) return -1;
            if (!propA.isOwner && propB.isOwner) return 1;
            return propA.name.localeCompare(b.name);
        });
    }, [groupedDevices]);

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="main-dashboard-content">
            <div className="dashboard-header">
                <h1>{t('dashboard.main.deviceOverview.title', 'Device Overview')}</h1>
            </div>

            {error && <div className="form-error" style={{ textAlign: 'center' }}>{error}</div>}

            {Object.keys(groupedDevices).length === 0 && !error && (
                <p className="no-devices-message">{t('devices.noDevicesFound')}</p>
            )}

            {sortedPropertyIds.map(propertyId => {
                const propertyGroup = groupedDevices[propertyId];
                if (!propertyGroup || !propertyGroup.devices) return null;
                const sortedDevices = [...propertyGroup.devices].sort((a, b) => a.device_name.localeCompare(b.device_name));

                return (
                    <div key={propertyId} className="property-device-group">
                        <h2 className="property-group-header">
                            {propertyGroup.name}
                            {!propertyGroup.isOwner && (
                                <span className="shared-property-indicator">({t('properties.shared')})</span>
                            )}
                        </h2>
                        <div className="devices-grid">
                            {sortedDevices.map(device => {
                                const canControl = propertyGroup.isOwner || propertyGroup.permissions.includes('control') || propertyGroup.permissions.includes('manage');
                                return (
                                    <DeviceCard
                                        key={device.device_id}
                                        device={device}
                                        canControl={canControl}
                                        onToggle={handleToggleDevice}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            <div className="dashboard-footer-copyright">
                &copy; {new Date().getFullYear()} beinSmart.cloud. {t('footer.allRightsReserved')}
            </div>
        </div>
    );
}

export default DashboardContent;
